import { initialsFrom } from "./initials.js";
import { preferredFirstName, preferredFirstNameFromProfile } from "./preferred-first-name.js";
export function trimmedString(value) {
    return typeof value === "string" && value.trim() ? value.trim() : null;
}
export function userInfoEndpoint(issuer) {
    return `${issuer.replace(/\/+$/, "")}/protocol/openid-connect/userinfo`;
}
/** One quick try, then one slower retry: 3 s at most. */
const USERINFO_ATTEMPT_TIMEOUTS_MS = [1_000, 2_000];
/**
 * The caller's Keycloak /userinfo claims. Timeouts, network errors and 5xx/429
 * are retried; throws on any other HTTP failure or a sub mismatch (OIDC Core 5.3.2).
 */
export async function fetchUserInfo(args) {
    let res;
    let lastError;
    for (const timeoutMs of USERINFO_ATTEMPT_TIMEOUTS_MS) {
        try {
            res = await (args.fetchImpl ?? fetch)(args.endpoint, {
                headers: { authorization: `Bearer ${args.accessToken}` },
                signal: AbortSignal.timeout(timeoutMs),
            });
        }
        catch (err) {
            lastError = err;
            res = undefined;
            continue;
        }
        if (res.status < 500 && res.status !== 429)
            break;
    }
    if (!res)
        throw lastError;
    if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`userinfo: HTTP ${res.status}${body ? ` - ${body}` : ""}`);
    }
    const info = (await res.json());
    if (info.sub !== args.userId)
        throw new Error("userinfo: sub does not match the caller");
    return info;
}
/**
 * From /userinfo: full name, then given + family, then the brokered IdP display
 * name (Teams); preferred_username only when it is not an email.
 */
export function memberNameFromUserInfo(info) {
    const composed = [trimmedString(info.given_name), trimmedString(info.family_name)]
        .filter(Boolean)
        .join(" ");
    const username = trimmedString(info.preferred_username);
    const fullName = trimmedString(info.name) ||
        composed ||
        trimmedString(info.azuread_displayName) ||
        trimmedString(info.sso_displayName) ||
        (username && !username.includes("@") ? username : null);
    return { fullName, firstName: preferredFirstNameFromProfile(info) ?? null };
}
/** First and last name to store, from /userinfo: given + family, else the full name split at the first space. */
export function storableNamesFromUserInfo(info) {
    const given = trimmedString(info.given_name);
    const family = trimmedString(info.family_name);
    if (given || family)
        return { firstName: given, lastName: family };
    const [first, ...rest] = (memberNameFromUserInfo(info).fullName ?? "").split(/\s+/);
    return { firstName: first || null, lastName: rest.join(" ") || null };
}
/** From the names stored on the member's `users` row. */
export function memberNameFromStored(firstName, lastName) {
    const fullName = [trimmedString(firstName), trimmedString(lastName)].filter(Boolean).join(" ");
    return {
        fullName: fullName || null,
        firstName: preferredFirstName({ givenName: firstName ?? null }) ?? null,
    };
}
export function memberInitials(name, fallback = "?") {
    return initialsFrom(name?.fullName ?? "", fallback);
}
//# sourceMappingURL=member-name.js.map